/**
 * @requirement S-041 AC #3 — "File-source badge visible after file
 *   selection (\"Local file · Guest\")".
 *
 * Small semantic badge shown once a file has been picked. Displays
 * the origin (local / drive / dropbox) and the auth mode (Guest /
 * signed-in). Consumers pass the resolved source + mode.
 */

import type { ReactElement } from 'react';

export type FileSource = 'local' | 'drive' | 'dropbox' | 'onedrive';
export type AuthMode = 'guest' | 'signed-in';

const SOURCE_LABEL: Record<FileSource, string> = {
  local: 'Local file',
  drive: 'Google Drive',
  dropbox: 'Dropbox',
  onedrive: 'OneDrive',
};

const MODE_LABEL: Record<AuthMode, string> = {
  guest: 'Guest',
  'signed-in': 'Signed in',
};

export function FileSourceBadge({
  source,
  mode,
}: {
  source: FileSource;
  mode: AuthMode;
}): ReactElement {
  return (
    <span
      role="status"
      aria-live="polite"
      className="inline-flex items-center gap-1 rounded-sm border border-chrome-border-subtle bg-chrome-surface px-2 py-0.5 text-xs text-chrome-text-secondary"
    >
      <span>{SOURCE_LABEL[source]}</span>
      <span aria-hidden="true">·</span>
      <span>{MODE_LABEL[mode]}</span>
    </span>
  );
}
